import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, filter, Observable, of, tap } from 'rxjs';
import { UserAdmin } from 'src/app/core/models/admin';
import { RouteSummaryResponse } from 'src/app/core/models/operations';
import {
  AddOnDemandPickupRequest,
  AddOnDemandPickupResponse,
} from 'src/app/core/models/operations/operations-on-demand-pickup.model';
import {
  OperationsPickup,
  SaveScheduledPickupRequest,
  SaveScheduledPickupResponse,
} from 'src/app/core/models/operations/operations-pickup.model';
import { RouteService } from 'src/app/core/services/admin/route/route.service';
import { PickupAssignmentsService } from 'src/app/core/services/operations/pickup-assignments/pickup-assignments.service';
import { SummaryService } from 'src/app/core/services/operations/summary/summary.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ServiceSupportUtils } from 'src/app/core/utils/service-support/service-support.utils';
import { BANNER_TYPE } from '../../banner/banner.constants';
import { IMAGE_SRC } from '../../shared/shared-constants';
import { AddPickupRequestComponent } from '../shared/add-pickup-request/add-pickup-request.component';
import { CONST } from './pickup-assignment.constants';

@Component({
  selector: 'app-pickup-assignment',
  templateUrl: './pickup-assignment.component.html',
  styleUrls: ['./pickup-assignment.component.scss'],
  standalone: false,
})
export class PickupAssignmentComponent implements OnInit {
  @Output()
  addPickupEvent = new EventEmitter<{
    response: SaveScheduledPickupResponse | AddOnDemandPickupResponse;
    data: SaveScheduledPickupRequest | AddOnDemandPickupRequest;
  }>();

  destroy = inject(DestroyRef);
  private readonly userService = inject(UserService);

  summaryData$!: Observable<RouteSummaryResponse>;
  updatedData$!: Observable<OperationsPickup[]>;
  pickupData!: OperationsPickup[];
  currLocation!: string;
  countryCode = '';
  resetSelectDropdowns = new BehaviorSubject<boolean>(false);
  exportExcelEvent = new BehaviorSubject<boolean>(false);
  filterDisabled = true;
  routes: string[] = [];

  date: Date = new Date();

  private dateChangeDueToLocation = false;
  private prevLocation: string | null = null;

  minDate: Date = new Date(
    this.date.getFullYear(),
    this.date.getMonth(),
    this.date.getDate() - 5,
  );

  maxDate: Date = new Date(
    this.date.getFullYear(),
    this.date.getMonth(),
    this.date.getDate() + 10,
  );

  readonly CONST = CONST;
  readonly IMAGES = IMAGE_SRC;
  summaryRoutes: string[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly pickupAssignmentsService: PickupAssignmentsService,
    private readonly commonUtils: CommonUtils,
    private readonly summaryService: SummaryService,
    private readonly serviceSupportUtils: ServiceSupportUtils,
    private readonly routeService: RouteService,
  ) {}

  ngOnInit() {
    this.refreshData(this.date);
  }

  clearFilter() {
    this.resetSelectDropdowns.next(true);
  }

  sendAddPickupSignal() {
    const dialogRef = this.dialog.open(AddPickupRequestComponent, {
      disableClose: true,
      data: {
        routes: this.summaryRoutes,
        addOnDemandPickup: true,
        countryCode: this.countryCode,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe(
        (value: {
          response: SaveScheduledPickupResponse | AddOnDemandPickupResponse;
          data: SaveScheduledPickupRequest | AddOnDemandPickupRequest;
        }) => {
          if (value) {
            this.addPickupEvent.emit(value);
          }
        },
      );
  }

  exportExcel() {
    this.exportExcelEvent.next(true);
  }

  enableClearFilterIcon(event: boolean) {
    this.filterDisabled = !event;
  }

  dateChanged(date: Date): void {
    if (this.dateChangeDueToLocation) {
      this.dateChangeDueToLocation = false;
    } else {
      this.date = date;
      this.refreshData(date);
    }
  }

  refreshData(_date: Date): void {
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        tap((user) => {
          const locationChanged = this.prevLocation !== user.location;
          this.prevLocation = user.location;

          if (locationChanged) {
            this.dateChangeDueToLocation = true;
            this.date = new Date(); // or any logic to reset date
          }

          this.summaryRoutes = [];
          this.currLocation = user.location;
          this.getAllPickups(user.location);
          this.getAllRoutes(user.location);
          this.getSummaryDashboard(this.date, user.location);
          this.clearFilter();
          this.dateChangeDueToLocation = false;
        }),
      )
      .subscribe();
  }

  getAllRoutes(location: string) {
    const routeSet = new Set<string>();

    this.routeService
      .getAllRoutes(location, BANNER_TYPE.ROUTES_ERROR)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((routes) => {
        routes?.response?.routes?.forEach((data) => {
          if (data.route_id) {
            routeSet.add(data.route_id);
          }
        });

        this.summaryRoutes = Array.from(routeSet).sort((a, b) =>
          a.localeCompare(b),
        );
      });
  }

  /**
   * Get data for summary dashboard (top right)
   *
   * @memberof PickupAssignmentComponent
   */
  getSummaryDashboard(date: Date, locationCode: string) {
    this.summaryService
      .getRouteSummary(
        locationCode,
        this.serviceSupportUtils.getMonthFirstFormattedDate(date),
      )
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((data) => {
        this.summaryData$ = of(data);
        data.response.routesummary.forEach((d) => {
          this.routes.push(d.route);
        });
      });
  }

  getAllPickups(locationCode: string) {
    this.pickupAssignmentsService
      .getAllPickups(this.commonUtils.getFormattedDate(this.date), locationCode)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((value) => {
        this.updatedData$ = of(value?.response?.pickupAssignments);
        const data = value?.response?.pickupAssignments;
        data.sort((a, b) => a.route?.localeCompare(b?.route));
        this.pickupData = data;
        this.countryCode = this.pickupData[0]?.countryCode;
      });
  }
}
